import {
  event_realtime_types,
  normalize_realtime_event_data,
  patch_event_in_all_lists,
  remove_event_from_all_lists,
  update_event_rsvp_for_current_user,
  update_event_rsvp_list_in_all_lists,
  update_event_rsvp_cancelled_in_all_lists,
  apply_patch_to_event_object,
} from "./realtimeEventHelpers";
import {
  update_event_ticket_purchased_in_all_lists,
  update_event_published_in_all_lists,
  can_insert_created_event,
} from "./realtimeEventSecondHelper";

function get_realtime_payload(dom_event) {
  const payload = dom_event?.detail;
  if (!payload || typeof payload !== "object") return null;
  return payload;
}

function get_realtime_event_type(payload) {
  const event_type = payload?.event_type || payload?.type;
  return event_type || null;
}

function get_list_names(options) {
  return Array.isArray(options?.list_names) ? options.list_names : undefined;
}

function is_event_management_payload(payload) {
  return !(payload?.module && payload.module !== "event_management");
}

function get_event_list_realtime_context(dom_event) {
  const payload = get_realtime_payload(dom_event);
  if (!payload) return null;
  if (!is_event_management_payload(payload)) return null;

  const event_type = get_realtime_event_type(payload);
  if (!event_type || !event_realtime_types.includes(event_type)) return null;

  const normalized = normalize_realtime_event_data(payload?.data);
  const event_id = normalized?.id;
  if (!event_id) return null;

  return { payload, event_type, normalized, event_id };
}

function is_supported_manage_event_type(event_type) {
  return [
    "event_updated",
    "event_deleted",
    "event_rsvp_updated",
    "event_rsvp_cancelled",
    "event_ticket_purchased",
    "event_published_updated",
    // Backend may emit `event_${action}` (published/unpublished)
    "event_published",
    "event_unpublished",
  ].includes(event_type);
}

function is_same_event(vm, normalized) {
  const event_id = Number(normalized?.id);
  const current_id = Number(vm?.event_id || vm?.event?.id || vm?.$route?.params?.event_id);
  if (!current_id || !event_id) return false;
  return current_id === event_id;
}

function apply_manage_event_patch(vm, normalized) {
  if (vm?.event && typeof vm.event === "object") {
    vm.event = apply_patch_to_event_object(vm.event, normalized);
  }
  if (vm?.selected_event && typeof vm.selected_event === "object") {
    vm.selected_event = apply_patch_to_event_object(vm.selected_event, normalized);
  }
}

function handle_manage_event_deleted(vm, payload) {
  // No navigation/reload here; keep the page but indicate the event is gone.
  vm.event = null;
  vm.selected_event = null;
  vm.error_message = payload?.message || "This event was deleted.";
}

function sync_manage_event_form_fields(vm) {
  // manage_event.vue uses many top-level form fields (event_name, start_date, etc.) that are
  // initialized from `vm.event` once on fetch. Keep them in sync on realtime updates, but
  // avoid stomping on local edits.
  if (vm?.has_unsaved_changes) return;

  const updated_event = vm?.event && typeof vm.event === "object" ? vm.event : null;
  if (!updated_event) return;

  const assign_if_present = (field, value) => {
    if (value === null || value === undefined) return;
    Reflect.set(vm, field, value);
  };

  assign_if_present("event_name", updated_event.event_name);
  assign_if_present("event_capacity", updated_event.event_capacity);
  assign_if_present("location", updated_event.location);
  assign_if_present("portal_id", updated_event.portal_id);
  assign_if_present("event_type", updated_event.event_type);
  assign_if_present("is_recurring", updated_event.is_recurring);
  assign_if_present("organizer", updated_event.organizer);
  assign_if_present("event_description", updated_event.event_description);
  assign_if_present("start_date", updated_event.start_date);
  assign_if_present("start_time", updated_event.start_time);
  assign_if_present("end_date", updated_event.end_date);
  assign_if_present("end_time", updated_event.end_time);
  assign_if_present("rsvp_start_date", updated_event.rsvp_start_date);
  assign_if_present("rsvp_start_time", updated_event.rsvp_start_time);
  assign_if_present("rsvp_end_date", updated_event.rsvp_end_date);
  assign_if_present("rsvp_end_time", updated_event.rsvp_end_time);
  assign_if_present("is_free", updated_event.is_free);
  assign_if_present("event_link", updated_event.event_link);
  assign_if_present("latitude", updated_event.latitude);
  assign_if_present("longitude", updated_event.longitude);
}

export function handle_event_list_realtime(vm, dom_event, options = {}) {
  const ctx = get_event_list_realtime_context(dom_event);
  if (!ctx) return;
  const { payload, event_type, normalized, event_id } = ctx;

  console.log("[realtime] event update:", payload?.data || payload);

  const list_names = get_list_names(options);

  const handlers = {
    event_deleted: () => {
      remove_event_from_all_lists(vm, event_id, list_names);
    },
    event_rsvp_updated: () => {
      // If backend includes a full serialized event object in `payload.data.event/event_data`,
      // `normalized` will contain updated RSVP lists/counts. Patch the event cards first,
      // then update the current user's RSVP flags.
      patch_event_in_all_lists(vm, event_id, normalized, list_names);
      update_event_rsvp_for_current_user(vm, event_id, normalized, list_names);
      update_event_rsvp_list_in_all_lists(vm, event_id, normalized, list_names);
    },
    event_rsvp_cancelled: () => {
      // Backend cancellation payload may only include { event_id, user_id }.
      update_event_rsvp_cancelled_in_all_lists(vm, event_id, normalized, list_names);
    },
    event_ticket_purchased: () => {
      // Backend purchase payload includes { event_id, ticket_id, quantity }.
      update_event_ticket_purchased_in_all_lists(vm, event_id, normalized, list_names);
    },
    event_published_updated: () => {
      update_event_published_in_all_lists(vm, event_id, normalized, list_names);
    },
    event_published: () => {
      update_event_published_in_all_lists(vm, event_id, normalized, list_names);
    },
    event_unpublished: () => {
      update_event_published_in_all_lists(vm, event_id, normalized, list_names);
    },
    event_updated: () => {
      patch_event_in_all_lists(vm, event_id, normalized, list_names);
    },
    event_created: () => {
      if (!can_insert_created_event(payload?.data, normalized)) {
        console.warn("[realtime] event_created payload missing fields for in-memory insert; skipping.", payload);
        return;
      }

      // TODO: implement insert once the backend payload includes all card fields consistently.
      console.warn("[realtime] event_created received but insert not implemented for partial payload.", payload);
    },
  };

  const handler = Reflect.get(handlers, event_type);
  if (typeof handler !== "function") return;
  handler();
}

export function handle_manage_event_realtime(vm, dom_event) {
  const payload = get_realtime_payload(dom_event);
  if (!payload) return;
  if (!is_event_management_payload(payload)) return;

  const event_type = get_realtime_event_type(payload);
  if (!is_supported_manage_event_type(event_type)) return;

  const normalized = normalize_realtime_event_data(payload?.data);
  if (!normalized) return;
  if (!is_same_event(vm, normalized)) return;

  console.log("[realtime] manage_event update:", payload?.data || payload);

  const after_patch_handlers = {
    event_rsvp_updated: () => {
      update_event_rsvp_list_in_all_lists(vm, normalized.id, normalized);
    },
    event_rsvp_cancelled: () => {
      update_event_rsvp_cancelled_in_all_lists(vm, normalized.id, normalized);
    },
    event_ticket_purchased: () => {
      update_event_ticket_purchased_in_all_lists(vm, normalized.id, normalized);
    },
  };

  const handlers = {
    event_deleted: () => {
      handle_manage_event_deleted(vm, payload);
    },
    _default: () => {
      apply_manage_event_patch(vm, normalized);

      const after_patch = Reflect.get(after_patch_handlers, event_type);
      if (typeof after_patch === "function") after_patch();

      sync_manage_event_form_fields(vm);
    },
  };

  const handler = Reflect.get(handlers, event_type) || handlers._default;
  handler();
}

// NOTE: snake_case exports are the supported API for this module:
// - `handle_event_list_realtime`
// - `handle_manage_event_realtime`

